import { FormGroup, ValidationErrors, ValidatorFn, AbstractControl, Validators } from '@angular/forms';

export class FormValidators {
  static markControlsAsTouched(form: FormGroup) {
    for (const key of Object.keys(form.controls)) {
      const control = form.get(key);
      if (control.invalid && !(control.dirty || control.touched)) {
        control.markAsTouched();
      }
    }
  }

  static getParent(control) {
    if (!control.parent) {
      return control;
    }

    return FormValidators.getParent(control.parent);
  }

  static validateRepeat(targetName): ValidatorFn {
    return (control: AbstractControl) => {
      const parent = control.parent;
      if (parent) {
        const targetValue = parent.get(targetName).value;
        const controlValue = control.value;
        return targetValue !== controlValue ? { repeat: true } : null;
      }
      return null;
    };
  }

  static noEqual(targetName): ValidatorFn {
    return (control: AbstractControl) => {
      const parent = control.parent;
      if (parent) {
        const targetValue = parent.get(targetName).value;
        const controlValue = control.value;

        const isEqual = targetValue === controlValue;
        if (isEqual) {
          return { noEqual: `Should not be equal to Field(${targetName})` };
        }
      }

      return null;
    };
  }

  static password(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl) => {
      const isValid = regex.test(control.value);
      return isValid ? null : error;
    };
  }

  static security(securityRules: { [key: string]: RegExp }): ValidatorFn {
    const securityRuleList = Object.keys(securityRules);

    return (control: AbstractControl) => {
      let hasError = false;

      const securityErrors = {};
      securityRuleList.forEach((securityRuleName: string) => {
        const isValid = securityRules[securityRuleName].test(control.value);
        if (!isValid) {
          securityErrors[securityRuleName] = true;
          hasError = true;
        }
      });

      return hasError ? { security: securityErrors } : null;
    };
  }

  static rangeNumber(min, max): ValidatorFn {
    return (control: AbstractControl) => {
      const isNotValid = control.value > max || control.value < min || !control.value;
      return isNotValid ? { 'rangeNumber': true }  : null;
    };
  }

  static mandatoryCheckbox(control: AbstractControl) {
    const isNotValid = control.value !== true;
    return isNotValid ? { mandatory: true }  : null;
  }

  static notOnlySymbols(regExp: RegExp): ValidatorFn {
    return (control: AbstractControl) => {
      const isValid = !control.value || regExp.test(control.value);
      return isValid ? null : { 'not_only_symbols': true };
    };
  }

  static patterns(patternList: RegExp[], matchMethod: 'some' | 'every') {
    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }

      const isValid = patternList[matchMethod]((regExp: RegExp) => regExp.test(control.value));
      return isValid ? null : { pattern: true };
    };
  }

  static minAge(minAge): ValidatorFn {
    return (control: AbstractControl) => {
      const today = new Date();
      const birthDate = new Date(control.value.split('/').reverse().join('/'));
      let age = today.getFullYear() - birthDate.getFullYear();
      const month = today.getMonth() - birthDate.getMonth();
      if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
          age--;
      }

      return age < minAge ?
            { 'minAge': {'requiredAge': minAge, 'actualAge': age} }:
            null;
      };
  }

  static pattern(pattern, patternName = 'pattern'): ValidatorFn {
    return (control: AbstractControl) => {
      return Validators.pattern(pattern)(control) ?
            { [patternName]: { 'requiredPattern': pattern, 'actualValue': control.value } } :
             null;
    };
  }
}
